import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Bot,
  ChevronDown,
  ChevronRight,
  Search,
  SlidersHorizontal,
  User,
  Wrench,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import type {
  ExecutionStatus,
  ExecutionType,
  RunDoc,
} from '@mct/shared';
import { normalizeCatalogOptions } from '@mct/shared';
import { useAuthState } from '../hooks/useAuth';
import {
  useAssignees,
  useBacklogCache,
  useExecutionsForRun,
  useProject,
  useRuns,
  useTasks,
} from '../hooks/useFirestoreData';
import { useWbsItems } from '../hooks/useWbs';
import { useUiStore } from '../store/uiStore';
import { RunSelector } from '../components/DagView/RunSelector';
import { ViewToggle } from '../components/ProjectWork/ViewToggle';
import { SIGNAL_LABEL } from '../components/DagView/signal';
import { StatusBadge } from '../components/StatusBadge';
import { statusPillClass } from '../lib/statusColors';
import { TaskDetailPanel } from '../components/TaskDetail/TaskDetailPanel';
import { AssigneeChip } from '../components/common/AssigneeChip';
import {
  HighlightControls,
  type HighlightAssigneeOption,
} from '../components/common/HighlightControls';
import { Badge } from '../components/ui/Badge';
import { buildLabelColorIndexMap } from '../lib/areaColor';
import { resolveAssigneeColorClasses } from '../lib/assigneeColor';
import {
  decodeHighlightFromParams,
  encodeHighlightToParams,
  isEmptyHighlight,
  isTaskHighlighted,
  UNASSIGNED_KEY,
  type HighlightState,
} from '../lib/highlight';
import { Spinner } from '../components/ui/Spinner';
import { Input } from '../components/ui/Input';
import { getDb } from '../lib/firebase';
import { deleteTask } from '../lib/firestore';
import { cn } from '../lib/utils';
import {
  buildColumnWidthCss,
  clampColumnWidth,
  loadColumnWidths,
  MIN_COLUMN_WIDTH,
  nextWidthFromDrag,
  resolveColumnWidth,
  saveColumnWidths,
  TASK_LIST_COLUMN_ORDER,
  type ColumnWidthMap,
  type TaskListColumnKey,
} from '../lib/taskListColumns';
import {
  DEFAULT_SORT,
  buildAppliedFilterChips,
  collectAreas,
  collectAssigneeOptions,
  collectCategories,
  collectPhaseLabels,
  collectScenarios,
  collectSystems,
  computeSummary,
  decodeFilterFromParams,
  encodeFilterToParams,
  filterRows,
  joinTasksAndExecutions,
  removeAppliedFilter,
  sortRows,
  type AppliedFilterChip,
  type SortKey,
  type TaskListFilter,
  type TaskListRow,
  type TaskListSummary,
} from '../lib/taskList';

const TYPE_ICON: Record<ExecutionType, React.ReactNode> = {
  automated: <Bot className="h-3.5 w-3.5" aria-label="自動" />,
  manual: <User className="h-3.5 w-3.5" aria-label="手動" />,
  hybrid: <Wrench className="h-3.5 w-3.5" aria-label="ハイブリッド" />,
};

const ALL_STATUSES: ExecutionStatus[] = [
  'not_started',
  'in_progress',
  'blocked',
  'done',
  'skipped',
];

export function TaskListPage() {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId) return null;
  return <TaskListContent projectId={projectId} />;
}

function TaskListContent({ projectId }: { projectId: string }) {
  const { user } = useAuthState();
  const { data: project } = useProject(projectId);
  const { data: tasks, loading: tasksLoading } = useTasks(projectId);
  const { data: runs } = useRuns(projectId);
  const { data: backlogCache } = useBacklogCache(projectId);
  const { data: assignees } = useAssignees(projectId);
  const { data: wbsItems } = useWbsItems(projectId);

  const selectedRunId = useUiStore(
    (s) => s.selectedRunIdByProject[projectId] ?? null,
  );
  const setSelectedRun = useUiStore((s) => s.setSelectedRun);
  const selectedTaskId = useUiStore((s) => s.selectedTaskId);
  const setSelectedTask = useUiStore((s) => s.setSelectedTask);

  // URL ↔ task selection sync: `?task=<id>` deep-links to the open
  // detail panel. Writes use `replace` so the Back button skips
  // intermediate selections.
  useEffect(() => {
    const fromUrl = searchParams.get('task');
    if (fromUrl && fromUrl !== selectedTaskId) {
      setSelectedTask(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (selectedTaskId) next.set('task', selectedTaskId);
        else next.delete('task');
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTaskId]);

  useEffect(() => {
    if (!selectedRunId && runs.length > 0) {
      const active = runs.find((r) => r.status === 'active') ?? runs[0]!;
      setSelectedRun(projectId, active.runId);
    }
  }, [selectedRunId, runs, projectId, setSelectedRun]);

  const selectedRun: RunDoc | null = useMemo(
    () => runs.find((r) => r.runId === selectedRunId) ?? null,
    [runs, selectedRunId],
  );

  const { data: executions, loading: execsLoading } = useExecutionsForRun(
    projectId,
    selectedRunId,
  );

  const backlogKeySet = useMemo(() => {
    const s = new Set<string>();
    for (const b of backlogCache) s.add(b.issueKey);
    return s;
  }, [backlogCache]);

  const [searchParams, setSearchParams] = useSearchParams();
  const { filter, sort } = useMemo(
    () => decodeFilterFromParams(searchParams),
    [searchParams],
  );
  const highlight = useMemo(
    () => decodeHighlightFromParams(searchParams),
    [searchParams],
  );

  const updateFilter = (next: TaskListFilter) => {
    setSearchParams(encodeFilterToParams(next, sort, searchParams), {
      replace: true,
    });
  };

  const updateHighlight = (next: HighlightState) => {
    setSearchParams(encodeHighlightToParams(next, searchParams), {
      replace: true,
    });
  };

  // 領域 catalog from `project.wbsCatalog.area` — matches the WBS surface
  // so the highlight chips paint with the same colours as the detail
  // panel's AreaChipPicker. Falls back to whatever area labels the rows
  // already carry so legacy projects without a stored catalog still get
  // a useful option list.
  const areaCatalog = useMemo(() => {
    const normalized = normalizeCatalogOptions(project?.wbsCatalog?.area);
    const byLabel = new Map<string, { label: string; colorIndex?: number }>();
    for (const opt of normalized) byLabel.set(opt.label, opt);
    return Array.from(byLabel.values());
  }, [project?.wbsCatalog?.area]);
  const areaColorIndices = useMemo(
    () =>
      buildLabelColorIndexMap(
        areaCatalog.map((o) => ({ id: o.label, label: o.label, colorIndex: o.colorIndex })),
      ),
    [areaCatalog],
  );

  const rows = useMemo(
    () =>
      joinTasksAndExecutions(
        tasks,
        executions,
        project,
        backlogKeySet,
        assignees,
      ),
    [tasks, executions, project, backlogKeySet, assignees],
  );

  const filteredRows = useMemo(() => filterRows(rows, filter), [rows, filter]);
  const sortedRows = useMemo(
    () => sortRows(filteredRows, sort),
    [filteredRows, sort],
  );

  const filteredSummary = useMemo(
    () => computeSummary(filteredRows),
    [filteredRows],
  );

  const categories = useMemo(() => collectCategories(rows), [rows]);
  const systems = useMemo(() => collectSystems(rows), [rows]);
  const areas = useMemo(() => collectAreas(rows), [rows]);
  const scenarios = useMemo(() => collectScenarios(rows), [rows]);
  const phaseLabels = useMemo(() => collectPhaseLabels(rows), [rows]);
  const assigneeOptions = useMemo(
    () => collectAssigneeOptions(rows),
    [rows],
  );

  // Highlight area / assignee option lists — union of catalog values and
  // values seen on actual rows. Catalog values come first so the menu
  // ordering matches the picker the user just used in the detail panel.
  const highlightAreaOptions = useMemo<string[]>(() => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const o of areaCatalog) {
      if (!seen.has(o.label)) {
        seen.add(o.label);
        out.push(o.label);
      }
    }
    for (const a of areas) {
      if (!seen.has(a)) {
        seen.add(a);
        out.push(a);
      }
    }
    return out;
  }, [areaCatalog, areas]);

  const highlightAssigneeOptions = useMemo<HighlightAssigneeOption[]>(() => {
    const seenIds = new Set<string>();
    const opts: HighlightAssigneeOption[] = [];
    for (const a of assignees) {
      seenIds.add(a.assigneeId);
      opts.push({
        value: a.assigneeId,
        label: a.name,
        colorIndex: a.colorIndex,
      });
    }
    let hasUnassigned = false;
    for (const r of rows) {
      if (!r.assigneeId) {
        hasUnassigned = true;
        continue;
      }
      if (!seenIds.has(r.assigneeId)) {
        seenIds.add(r.assigneeId);
        opts.push({
          value: r.assigneeId,
          label: r.assignee?.name ?? r.assigneeId,
          colorIndex: r.assignee?.colorIndex,
        });
      }
    }
    opts.sort((a, b) => a.label.localeCompare(b.label, 'ja'));
    if (hasUnassigned) {
      opts.push({ value: UNASSIGNED_KEY, label: '(未割当)' });
    }
    return opts;
  }, [assignees, rows]);

  const highlightActive = !isEmptyHighlight(highlight);

  const selectedTask = useMemo(
    () => tasks.find((t) => t.taskId === selectedTaskId) ?? null,
    [tasks, selectedTaskId],
  );

  const toggleSort = (key: SortKey) => {
    const next =
      sort.key === key
        ? { key, dir: sort.dir === 'asc' ? ('desc' as const) : ('asc' as const) }
        : { key, dir: 'asc' as const };
    setSearchParams(encodeFilterToParams(filter, next, searchParams), {
      replace: true,
    });
  };

  const clearFilters = () => {
    setSearchParams(
      encodeFilterToParams(
        {
          categories: [],
          assignees: [],
          statuses: [],
          systems: [],
          areas: [],
          scenarios: [],
          phaseLabels: [],
          search: '',
        },
        DEFAULT_SORT,
        searchParams,
      ),
      { replace: true },
    );
  };

  const activeFilterCount =
    filter.categories.length +
    filter.assignees.length +
    filter.statuses.length +
    filter.systems.length +
    filter.areas.length +
    filter.scenarios.length +
    filter.phaseLabels.length +
    (filter.search ? 1 : 0);

  // Applied-filter chips rendered inline next to the「フィルタ」toggle so
  // the active conditions stay visible while the panel is collapsed. The
  // assignee id → display-name map reuses the same option list the panel
  // builds; status labels come from the shared signal labels.
  const assigneeLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of assigneeOptions) m.set(o.value, o.label);
    return m;
  }, [assigneeOptions]);
  const appliedChips = useMemo(
    () =>
      buildAppliedFilterChips(filter, {
        status: (s) => SIGNAL_LABEL[s],
        assignee: (id) => assigneeLabelById.get(id) ?? id,
      }),
    [filter, assigneeLabelById],
  );
  const removeChip = (chip: AppliedFilterChip) => {
    updateFilter(removeAppliedFilter(filter, chip.dimension, chip.value));
  };

  // Phase 2.2 responsive: the chip filter row is rich (status + category +
  // 担当 + フェーズ + シナリオ + system + area), so on a phone it would
  // dominate the viewport. Collapse it behind a "フィルタ" toggle below
  // `sm`; the action stays expanded at tablet+ widths. The toggle counter
  // surfaces the active-filter count so users don't lose context when
  // it's collapsed.
  const [filterOpen, setFilterOpen] = useState(false);

  // Per-column widths, drag-resized via the header-edge handles and
  // persisted to localStorage (browser-scoped). Applied through a scoped
  // <style> block (see `columnWidthCss`) rather than inline `th` widths
  // so the existing source-text markup tests stay valid.
  const [columnWidths, setColumnWidths] = useState<ColumnWidthMap>(() =>
    loadColumnWidths(),
  );
  useEffect(() => {
    saveColumnWidths(columnWidths);
  }, [columnWidths]);

  const columnWidthCss = useMemo(
    () => buildColumnWidthCss(columnWidths, 'task-list-table'),
    [columnWidths],
  );

  // mousedown on a header-edge handle → live width follow on mousemove,
  // commit on mouseup. Body cursor / selection are overridden for the
  // duration so the drag reads cleanly and doesn't select header text.
  const beginColumnResize = useCallback(
    (e: React.MouseEvent, key: TaskListColumnKey) => {
      e.preventDefault();
      e.stopPropagation();
      const th = (e.currentTarget as HTMLElement).closest('th');
      const startWidth = th
        ? th.getBoundingClientRect().width
        : (resolveColumnWidth(columnWidths, key) ?? 160);
      const startX = e.clientX;
      const onMove = (ev: MouseEvent) => {
        const next = nextWidthFromDrag(startWidth, ev.clientX - startX);
        setColumnWidths((prev) => ({ ...prev, [key]: next }));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [columnWidths],
  );

  // Double-click a handle → "auto width": size the column to the widest
  // rendered cell content (header + body) in that column, clamped.
  const autoFitColumn = useCallback(
    (e: React.MouseEvent, key: TaskListColumnKey) => {
      e.preventDefault();
      e.stopPropagation();
      const table = (e.currentTarget as HTMLElement).closest('table');
      if (!table) return;
      const colIndex = TASK_LIST_COLUMN_ORDER.indexOf(key);
      if (colIndex < 0) return;
      let widest = MIN_COLUMN_WIDTH;
      const headerCell = table.querySelectorAll('thead th')[colIndex] as
        | HTMLElement
        | undefined;
      // +24 ≈ the cell's horizontal padding (px-3 → 12px each side) so the
      // content isn't clipped flush against the border.
      if (headerCell) widest = Math.max(widest, headerCell.scrollWidth + 24);
      table.querySelectorAll('tbody tr').forEach((tr) => {
        const cell = tr.children[colIndex] as HTMLElement | undefined;
        if (cell) widest = Math.max(widest, cell.scrollWidth + 24);
      });
      setColumnWidths((prev) => ({ ...prev, [key]: clampColumnWidth(widest) }));
    },
    [],
  );

  const loading = tasksLoading || (selectedRunId !== null && execsLoading);

  return (
    <div className="relative flex h-full overflow-hidden">
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div
          data-testid="task-list-header"
          className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-2 sm:gap-3 sm:px-4"
        >
          <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
            <ViewToggle />
            <RunSelector
              runs={runs}
              selectedRunId={selectedRunId}
              onChange={(id) => setSelectedRun(projectId, id)}
            />
          </div>
          <div className="flex flex-1 items-center justify-end gap-2 sm:flex-none">
            {selectedRun && !selectedRun.executionsReady ? (
              <span className="hidden text-xs text-slate-500 sm:inline">
                Execution 生成中… {selectedRun.executionsGeneratedCount}/
                {selectedRun.expectedExecutionsCount}
              </span>
            ) : null}
            <div className="relative w-full sm:w-auto">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <Input
                className="w-full pl-7 sm:!w-64"
                placeholder="名前・説明で検索"
                value={filter.search}
                onChange={(e) =>
                  updateFilter({ ...filter, search: e.target.value })
                }
              />
            </div>
          </div>
        </div>

        <ProgressSummary summary={filteredSummary} />

        {/*
         * Highlight (≠ filter) controls. The chip rail dims non-matching
         * rows but keeps them in the list so structure is preserved
         * (counts, scrolling). Lives in the header strip so users can
         * open the dropdowns without losing the table view.
         */}
        <div className="border-b border-slate-200 bg-white px-3 py-1.5 sm:px-4">
          <HighlightControls
            state={highlight}
            onChange={updateHighlight}
            areaOptions={highlightAreaOptions}
            areaColorIndices={areaColorIndices}
            assigneeOptions={highlightAssigneeOptions}
          />
        </div>

        {/*
         * Filter accordion toggle: collapses the chip rail behind a button
         * across all viewport widths so the table starts visible. The chip
         * rail (next div) is shown only when `filterOpen` is true. The
         * activeFilterCount surfaces as a badge so the user keeps context
         * when the rail is collapsed. The "フィルタをクリア" action moves
         * into the open rail itself so it only appears when expanded.
         */}
        <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-1.5 sm:px-4">
          <button
            type="button"
            data-testid="task-list-filter-toggle"
            onClick={() => setFilterOpen((v) => !v)}
            // 44px floor on touch surfaces (`max-lg:`) keeps the chevron
            // thumb-tappable; compact 32px at `lg+` keeps the header dense.
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded border px-2 py-1 text-xs transition-colors max-lg:min-h-[44px] max-lg:min-w-[44px] max-lg:px-3',
              activeFilterCount > 0
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100',
            )}
            aria-expanded={filterOpen}
            aria-controls="task-list-filter-bar"
          >
            {filterOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            <SlidersHorizontal className="h-3.5 w-3.5" />
            <span>フィルタ</span>
            {activeFilterCount > 0 ? (
              <span
                data-testid="task-list-filter-count"
                className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-white px-1.5 text-[11px] font-semibold tabular-nums text-slate-900"
              >
                {activeFilterCount}
              </span>
            ) : null}
          </button>

          {/*
           * Inline applied-filter chips: a wrapping summary of the active
           * conditions, shown only while the panel is COLLAPSED — when it's
           * open the panel's own chip groups already carry the selection
           * state, so the inline copy would duplicate it. Chips wrap (rather
           * than scroll) so the hover ✗ — which floats 1px past the chip's
           * top-right corner — is never clipped by an overflow box. Each ✗
           * drops just that condition; a trailing「クリア」link wipes them all.
           */}
          {!filterOpen && appliedChips.length > 0 ? (
            <div
              data-testid="task-list-applied-filters"
              className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5"
            >
              {appliedChips.map((chip) => (
                <AppliedFilterChipPill
                  key={chip.key}
                  chip={chip}
                  onRemove={() => removeChip(chip)}
                />
              ))}
              <button
                type="button"
                data-testid="task-list-applied-filters-clear"
                onClick={clearFilters}
                className="ml-1 shrink-0 whitespace-nowrap text-[11px] text-slate-500 hover:text-slate-900"
              >
                クリア ({activeFilterCount})
              </button>
            </div>
          ) : null}
        </div>

        <div
          id="task-list-filter-bar"
          data-testid="task-list-filter-bar"
          className={cn(
            'flex-wrap gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs sm:px-4',
            filterOpen ? 'flex' : 'hidden',
          )}
        >
          <FilterChipGroup
            label="ステータス"
            options={ALL_STATUSES.map((s) => ({
              value: s,
              label: SIGNAL_LABEL[s],
              tintClass: statusPillClass(s),
            }))}
            selected={filter.statuses}
            onToggle={(value) =>
              updateFilter({
                ...filter,
                statuses: toggle(filter.statuses, value as ExecutionStatus),
              })
            }
          />
          {categories.length > 0 ? (
            <FilterChipGroup
              label="カテゴリ"
              options={categories.map((c) => ({ value: c, label: c }))}
              selected={filter.categories}
              onToggle={(value) =>
                updateFilter({
                  ...filter,
                  categories: toggle(filter.categories, value),
                })
              }
            />
          ) : null}
          {assigneeOptions.length > 0 ? (
            <FilterChipGroup
              label="担当"
              options={assigneeOptions}
              selected={filter.assignees}
              onToggle={(value) =>
                updateFilter({
                  ...filter,
                  assignees: toggle(filter.assignees, value),
                })
              }
            />
          ) : null}
          {phaseLabels.length > 0 ? (
            <FilterChipGroup
              label="対象フェーズ"
              options={phaseLabels.map((p) => ({ value: p, label: p }))}
              selected={filter.phaseLabels}
              onToggle={(value) =>
                updateFilter({
                  ...filter,
                  phaseLabels: toggle(filter.phaseLabels, value),
                })
              }
            />
          ) : null}
          {scenarios.length > 0 ? (
            <FilterChipGroup
              label="シナリオ"
              options={scenarios.map((s) => ({ value: s, label: s }))}
              selected={filter.scenarios}
              onToggle={(value) =>
                updateFilter({
                  ...filter,
                  scenarios: toggle(filter.scenarios, value),
                })
              }
            />
          ) : null}
          {systems.length > 0 ? (
            <FilterChipGroup
              label="システム"
              options={systems.map((s) => ({ value: s, label: s }))}
              selected={filter.systems}
              onToggle={(value) =>
                updateFilter({
                  ...filter,
                  systems: toggle(filter.systems, value),
                })
              }
            />
          ) : null}
          {areas.length > 0 ? (
            <FilterChipGroup
              label="領域"
              options={areas.map((a) => ({ value: a, label: a }))}
              selected={filter.areas}
              onToggle={(value) =>
                updateFilter({
                  ...filter,
                  areas: toggle(filter.areas, value),
                })
              }
            />
          ) : null}
          {activeFilterCount > 0 ? (
            <button
              className="ml-auto inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
              onClick={clearFilters}
            >
              <X className="h-3 w-3" />
              フィルタをクリア ({activeFilterCount})
            </button>
          ) : null}
        </div>

        <PhaseCompletionCriteriaBanner
          projectId={projectId}
          selectedPhaseLabels={filter.phaseLabels}
          wbsItems={wbsItems}
        />

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          ) : tasks.length === 0 ? (
            <EmptyState message="このプロジェクトにはタスクが登録されていません。DAG ビューから追加してください。" />
          ) : sortedRows.length === 0 ? (
            <EmptyState message="条件に一致するタスクはありません。フィルタを変更してください。" />
          ) : (
            <>
            {/*
             * Column widths (+ the `position: relative` the resize handles
             * anchor to) are applied via `th:nth-child(n)` rather than
             * per-`th` inline styles. This keeps the source-text markup
             * tests valid and sidesteps the phantom-column a <colgroup>
             * would leave behind for responsive-hidden columns.
             */}
            <style>{columnWidthCss}</style>
            <table
              data-testid="task-list-table"
              className="w-full table-fixed border-separate border-spacing-0 text-sm"
            >
              <thead className="sticky top-0 z-10 bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-700 shadow-[0_1px_0_rgba(0,0,0,0.06)]">
                <tr>
                  {/*
                   * table-fixed with explicit pixel widths on non-name
                   * columns; the name column omits a width so it
                   * absorbs the remainder. This keeps short-text columns
                   * (ステータス / 担当) from collapsing to a single
                   * character per line regardless of viewport width,
                   * while still letting the long-text column scale.
                   *
                   * Header order MUST match TASK_LIST_COLUMN_ORDER so the
                   * nth-child width stylesheet lines up:
                   * No · ステータス · カテゴリ · タスク名 · 領域 ·
                   * sys From · sys To · 担当 · 方式 · 予定開始日 ·
                   * 完了日 · 説明 · Backlog.
                   */}
                  {/*
                   * 「No」= the xlsx source row key (task.sourceTaskId,
                   * e.g. "TT-0001"). Leftmost, fixed ~84px, font-mono so
                   * the codes align. Hidden below `sm` to keep phone rows
                   * scannable — same tier policy as 方式 / 完了日.
                   */}
                  <SortableTh
                    label="No"
                    sortKey="sourceTaskId"
                    currentSort={sort}
                    onToggle={toggleSort}
                    className="hidden sm:table-cell sm:w-[84px]"
                    resizeKey="sourceTaskId"
                    onResizeStart={beginColumnResize}
                    onAutoFit={autoFitColumn}
                  />
                  {/*
                   * ステータス sits directly right of No for immediate
                   * at-a-glance scanning — always visible (no tier gate).
                   */}
                  <SortableTh
                    label="ステータス"
                    sortKey="status"
                    currentSort={sort}
                    onToggle={toggleSort}
                    className="w-[110px]"
                    resizeKey="status"
                    onResizeStart={beginColumnResize}
                    onAutoFit={autoFitColumn}
                  />
                  {/* カテゴリ moves left of タスク名 (xlsx C→E order). */}
                  <SortableTh
                    label="カテゴリ"
                    sortKey="category"
                    currentSort={sort}
                    onToggle={toggleSort}
                    className="hidden lg:table-cell lg:w-[160px]"
                    resizeKey="category"
                    onResizeStart={beginColumnResize}
                    onAutoFit={autoFitColumn}
                  />
                  <SortableTh
                    label="タスク名"
                    sortKey="name"
                    currentSort={sort}
                    onToggle={toggleSort}
                    className="w-[42%] sm:w-auto"
                    resizeKey="name"
                    onResizeStart={beginColumnResize}
                    onAutoFit={autoFitColumn}
                  />
                  {/*
                   * 領域 / sys From / sys To — migration-source columns
                   * imported from the xlsx, surfaced right of タスク名.
                   * 領域 renders the resolved `wbsCatalog.area` labels;
                   * sys From / To are free-text source/destination systems.
                   * Desktop-tier (lg+) to keep phone/tablet rows scannable.
                   */}
                  <SortableTh
                    label="領域"
                    sortKey="area"
                    currentSort={sort}
                    onToggle={toggleSort}
                    className="hidden lg:table-cell lg:w-[110px]"
                    resizeKey="area"
                    onResizeStart={beginColumnResize}
                    onAutoFit={autoFitColumn}
                  />
                  <SortableTh
                    label="sys From"
                    sortKey="sysFrom"
                    currentSort={sort}
                    onToggle={toggleSort}
                    className="hidden lg:table-cell lg:w-[100px]"
                    resizeKey="sysFrom"
                    onResizeStart={beginColumnResize}
                    onAutoFit={autoFitColumn}
                  />
                  <SortableTh
                    label="sys To"
                    sortKey="sysTo"
                    currentSort={sort}
                    onToggle={toggleSort}
                    className="hidden lg:table-cell lg:w-[100px]"
                    resizeKey="sysTo"
                    onResizeStart={beginColumnResize}
                    onAutoFit={autoFitColumn}
                  />
                  <SortableTh
                    label="担当"
                    sortKey="assignee"
                    currentSort={sort}
                    onToggle={toggleSort}
                    className="w-[90px]"
                    resizeKey="assignee"
                    onResizeStart={beginColumnResize}
                    onAutoFit={autoFitColumn}
                  />
                  <th className="hidden border-b border-slate-200 px-3 py-2 font-medium sm:table-cell sm:w-[80px]">
                    方式
                    <ColResizeHandle
                      columnKey="executionType"
                      onResizeStart={beginColumnResize}
                      onAutoFit={autoFitColumn}
                    />
                  </th>
                  {/*
                   * 予定開始日 = the current run's per-task planned start
                   * (ExecutionDoc.plannedStartDate, an ISO date string).
                   * Mirrors the 完了日 column's tier / width / sort
                   * pattern and sits directly to its left.
                   */}
                  <SortableTh
                    label="予定開始日"
                    sortKey="plannedStartDate"
                    currentSort={sort}
                    onToggle={toggleSort}
                    className="hidden sm:table-cell sm:w-[110px]"
                    resizeKey="plannedStartDate"
                    onResizeStart={beginColumnResize}
                    onAutoFit={autoFitColumn}
                  />
                  <SortableTh
                    label="完了日"
                    sortKey="completedAt"
                    currentSort={sort}
                    onToggle={toggleSort}
                    className="hidden sm:table-cell sm:w-[110px]"
                    resizeKey="completedAt"
                    onResizeStart={beginColumnResize}
                    onAutoFit={autoFitColumn}
                  />
                  {/* 説明 moves to the far right (last content column). */}
                  <th className="hidden border-b border-slate-200 px-3 py-2 font-medium lg:table-cell lg:w-[280px]">
                    説明
                    <ColResizeHandle
                      columnKey="description"
                      onResizeStart={beginColumnResize}
                      onAutoFit={autoFitColumn}
                    />
                  </th>
                  <th className="hidden border-b border-slate-200 px-3 py-2 font-medium lg:table-cell lg:w-[110px]">
                    Backlog
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => {
                  const highlighted = isTaskHighlighted(
                    { areaId: row.area, assigneeId: row.assigneeId },
                    highlight,
                  );
                  // Ring colour follows the assignee's palette when the
                  // task is highlighted — same hue the AssigneeChip
                  // would render — so the emphasis reads as "this is
                  // 鈴木's work" at a glance. Falls back to a neutral
                  // slate ring when no assignee is set.
                  const ringClasses = row.assigneeId
                    ? resolveAssigneeColorClasses(
                        row.assignee
                          ? { colorIndex: row.assignee.colorIndex }
                          : null,
                        row.assignee?.name ?? null,
                      )
                    : 'bg-slate-100 text-slate-500';
                  // Strip the bg-* prefix (`bg-red-100 text-red-800`) →
                  // ring-red-300 so the saturation isn't doubled by the
                  // pastel pill background.
                  const ringColor = ringClasses
                    .split(' ')
                    .find((cls) => cls.startsWith('bg-'))
                    ?.replace(/^bg-/, 'ring-')
                    .replace(/-100$/, '-300');
                  return (
                    <TaskRow
                      key={row.taskId}
                      row={row}
                      active={row.taskId === selectedTaskId}
                      onClick={() => setSelectedTask(row.taskId)}
                      dimmed={highlightActive && !highlighted}
                      highlighted={highlightActive && highlighted}
                      ringColorClass={ringColor ?? null}
                    />
                  );
                })}
              </tbody>
            </table>
            </>
          )}
        </div>
      </div>

      {/*
       * Detail panel as a RIGHT-EDGE OVERLAY layer. Previously the panel
       * was an in-flow flex sibling (`lg:shrink-0` + inline width), so
       * opening it shrank the list — the long タスク名 / 説明 columns
       * collapsed and overlapped. Wrapping it in an `absolute inset-y-0
       * right-0` layer lifts it out of the flex flow: the list keeps its
       * full width (table-fixed widths stay constant) and the panel
       * slides over the rightmost columns instead. No backdrop — the list
       * stays readable to the left; the boundary reads via `shadow-xl`.
       *
       * Below `lg` the panel's own `fixed inset-0 z-50` shell takes over
       * (full-screen modal), so this absolute wrapper only governs the
       * desktop side-overlay geometry.
       *
       * z-30 clears the sticky table header (z-10) but stays below the
       * panel's mobile `z-50`.
       */}
      {selectedTask && project ? (
        <div
          data-testid="task-detail-overlay"
          className="absolute inset-y-0 right-0 z-30 flex max-w-full shadow-xl"
        >
          <TaskDetailPanel
            project={project}
            task={selectedTask}
            allTasks={tasks}
            currentRun={selectedRun}
            onClose={() => setSelectedTask(null)}
            onSelectTask={(id) => setSelectedTask(id)}
            onDelete={async () => {
              if (!user) return;
              if (
                !window.confirm(
                  'このタスクを削除しますか？関連する実行レコードも削除されます。',
                )
              ) {
                return;
              }
              await deleteTask(getDb(), projectId, selectedTask.taskId);
              setSelectedTask(null);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

function toggle<T>(list: readonly T[], value: T): T[] {
  return list.includes(value)
    ? list.filter((v) => v !== value)
    : [...list, value];
}

function SortableTh({
  label,
  sortKey,
  currentSort,
  onToggle,
  className,
  resizeKey,
  onResizeStart,
  onAutoFit,
}: {
  label: string;
  sortKey: SortKey;
  currentSort: { key: SortKey; dir: 'asc' | 'desc' };
  onToggle: (key: SortKey) => void;
  className?: string;
  /** When set, renders a right-edge drag handle that resizes this column. */
  resizeKey?: TaskListColumnKey;
  onResizeStart?: (e: React.MouseEvent, key: TaskListColumnKey) => void;
  onAutoFit?: (e: React.MouseEvent, key: TaskListColumnKey) => void;
}) {
  const active = currentSort.key === sortKey;
  return (
    <th
      scope="col"
      className={cn(
        'border-b border-slate-200 px-3 py-2 font-medium',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className={cn(
          'flex items-center gap-1 whitespace-nowrap hover:text-slate-900',
          active ? 'text-slate-900' : 'text-slate-600',
        )}
      >
        {label}
        {active ? (
          currentSort.dir === 'asc' ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </button>
      {resizeKey && onResizeStart ? (
        <ColResizeHandle
          columnKey={resizeKey}
          onResizeStart={onResizeStart}
          onAutoFit={onAutoFit}
        />
      ) : null}
    </th>
  );
}

/**
 * Right-edge drag handle for a resizable column header. Sits in the
 * rightmost ~6px of the `<th>` (which is `position: relative` via the
 * scoped width stylesheet) and is invisible until hovered, where it
 * paints a subtle indigo wash to advertise drag-ability. mousedown
 * starts a resize; double-click auto-fits to content. `stopPropagation`
 * on click keeps the header's sort toggle from firing on a drag.
 */
function ColResizeHandle({
  columnKey,
  onResizeStart,
  onAutoFit,
}: {
  columnKey: TaskListColumnKey;
  onResizeStart: (e: React.MouseEvent, key: TaskListColumnKey) => void;
  onAutoFit?: (e: React.MouseEvent, key: TaskListColumnKey) => void;
}) {
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label="列幅をドラッグして変更"
      data-testid={`col-resize-handle-${columnKey}`}
      onMouseDown={(e) => onResizeStart(e, columnKey)}
      onDoubleClick={(e) => onAutoFit?.(e, columnKey)}
      onClick={(e) => e.stopPropagation()}
      className="absolute right-0 top-0 z-20 h-full w-1.5 cursor-col-resize select-none hover:bg-indigo-300/40"
    />
  );
}

function TaskRow({
  row,
  active,
  onClick,
  dimmed = false,
  highlighted = false,
  ringColorClass = null,
}: {
  row: TaskListRow;
  active: boolean;
  onClick: () => void;
  /** True when the highlight controls are active and this row does NOT
   *  match. Renders the row at low opacity + grayscale so it visually
   *  recedes without losing its slot in the table. */
  dimmed?: boolean;
  /** True when the highlight controls are active and this row matches.
   *  Renders an outline ring in the assignee's colour. */
  highlighted?: boolean;
  /** Tailwind ring colour utility (e.g. `ring-blue-300`) for the
   *  highlighted-row outline. */
  ringColorClass?: string | null;
}) {
  return (
    <tr
      data-testid="task-list-row"
      data-highlighted={highlighted ? 'true' : undefined}
      data-dimmed={dimmed ? 'true' : undefined}
      className={cn(
        'cursor-pointer border-b border-slate-100',
        // Completed tasks get a subtle slate-50 background (same gradient
        // pattern as the WBS tree: active task → white, done task →
        // slate-50, selected row → slate-100). Hovers nudge each up
        // one step.
        active
          ? 'bg-slate-100'
          : row.status === 'done'
            ? 'bg-slate-50 hover:bg-slate-100'
            : 'bg-white hover:bg-slate-50',
        dimmed ? 'opacity-50 grayscale' : '',
        highlighted && ringColorClass
          ? `ring-2 ring-inset ${ringColorClass}`
          : '',
      )}
      onClick={onClick}
    >
      <td className="hidden truncate px-3 py-2 align-top text-center font-mono text-xs text-slate-600 sm:table-cell">
        {row.sourceTaskId ? (
          (() => {
            // Tatsuno migration project: sourceTaskId 'TT-NNNN' corresponds
            // to xlsx row NNNN+1 (header at row 1). The C column holds the
            // 中項目 (作業内容). Linking back lets the user jump straight
            // to the source row in the procedure spreadsheet.
            const m = row.sourceTaskId.match(/^TT-(\d+)$/);
            if (!m) return row.sourceTaskId;
            const xlsxRow = parseInt(m[1]!, 10) + 1;
            const href = `https://docs.google.com/spreadsheets/d/1pKzSzAz_9UOZsv3dszK7t4cHsJ_SgCmDdjCFpCEp0VM/edit?gid=1964657019#gid=1964657019&range=C${xlsxRow}`;
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-indigo-600 hover:text-indigo-800 hover:underline"
                title={`スプレッドシート row ${xlsxRow} を開く`}
              >
                {row.sourceTaskId}
              </a>
            );
          })()
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-2 align-top">
        <StatusBadge status={row.status} />
      </td>
      <td className="hidden truncate px-3 py-2 align-top text-slate-600 lg:table-cell">
        {row.category || <span className="text-slate-400">—</span>}
      </td>
      <td className="px-3 py-2 align-top">
        <div className="flex items-start gap-2">
          {row.status === 'blocked' ? (
            <span className="mt-1 text-red-600" aria-label="ブロック">⚠️</span>
          ) : null}
          <div className="min-w-0">
            {/*
             * Phase 2.3: clamp to 2 lines on mobile so long migration
             * task names ("〇〇システムの□□マスタを■■に移送するバッチ…")
             * stay legible without dominating the row. Desktop stays
             * single-line via `lg:line-clamp-1` to keep table density.
             * `title` surfaces the full name on hover-capable devices.
             */}
            <div
              className="font-medium text-slate-900 line-clamp-2 lg:line-clamp-1 lg:truncate"
              title={row.name}
            >
              {row.name}
            </div>
            {/*
             * Mobile-only inline meta: category + 完了 hint surface here at
             * `< sm` so the row still carries enough context after the
             * dedicated columns drop. Description is shown only as the
             * dedicated `説明` column at lg+ — at smaller widths it's
             * dropped to keep rows scannable.
             */}
            <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-slate-500 sm:hidden">
              {row.category ? (
                <span className="rounded bg-slate-100 px-1">{row.category}</span>
              ) : null}
              {row.completedAt ? (
                <span className="tabular-nums">
                  ✓ {format(row.completedAt, 'M/d')}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </td>
      {/*
       * 領域 — the task's resolved `wbsCatalog.area` labels (comma-joined,
       * `、` as the separator). `title` surfaces the full list when the
       * cell clamps; em-dash when the task carries no 領域.
       */}
      <td className="hidden truncate px-3 py-2 align-top text-slate-600 lg:table-cell">
        {row.areaLabels.length > 0 ? (
          <div
            className="truncate text-xs"
            title={row.areaLabels.join('、')}
          >
            {row.areaLabels.join('、')}
          </div>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="hidden truncate px-3 py-2 align-top text-slate-600 lg:table-cell">
        {row.sysFrom ? (
          <span className="text-xs" title={row.sysFrom}>
            {row.sysFrom}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="hidden truncate px-3 py-2 align-top text-slate-600 lg:table-cell">
        {row.sysTo ? (
          <span className="text-xs" title={row.sysTo}>
            {row.sysTo}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="truncate px-3 py-2 align-top text-slate-600">
        {row.assignee ? (
          <AssigneeChip assignee={row.assignee} />
        ) : (
          <span className="text-slate-400">(未割当)</span>
        )}
      </td>
      <td className="hidden truncate px-3 py-2 align-top text-slate-600 sm:table-cell">
        <span className="inline-flex items-center gap-1">
          {TYPE_ICON[row.executionType]}
          <span className="text-xs">{row.executionType}</span>
        </span>
      </td>
      <td className="hidden truncate px-3 py-2 align-top text-slate-600 sm:table-cell">
        {row.plannedStartDate ? (
          <span className="tabular-nums">{row.plannedStartDate}</span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="hidden truncate px-3 py-2 align-top text-slate-600 sm:table-cell">
        {row.completedAt ? (
          format(row.completedAt, 'yyyy/MM/dd')
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="hidden px-3 py-2 align-top text-slate-600 lg:table-cell">
        {row.description ? (
          <div className="line-clamp-2 text-xs" title={row.description}>
            {row.description}
          </div>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="hidden px-3 py-2 align-top lg:table-cell">
        {row.backlogCount > 0 ? (
          <Badge tone="blue">Backlog:{row.backlogCount}</Badge>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
    </tr>
  );
}

function ProgressSummary({ summary }: { summary: TaskListSummary }) {
  const blocked = summary.blocked;
  const pct = Math.round(summary.progressRatio * 100);
  return (
    <div
      data-testid="task-list-summary"
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-slate-200 bg-white px-3 py-2 text-xs sm:gap-4 sm:px-4 sm:py-3"
    >
      <SummaryCounter label="総数" value={summary.total} />
      <SummaryCounter label="完了" value={summary.done} tone="green" />
      <SummaryCounter label="進行中" value={summary.inProgress} tone="yellow" />
      <SummaryCounter
        label="ブロック"
        value={blocked}
        tone={blocked > 0 ? 'red' : 'slate'}
        emphasise={blocked > 0}
      />
      {/* Below `sm` we drop 未着手 / スキップ from the rail since they
          are always derivable as `total - done - inProgress - blocked`
          and would otherwise wrap into a third row on phones. */}
      <div className="hidden sm:contents">
        <SummaryCounter label="未着手" value={summary.notStarted} />
        <SummaryCounter label="スキップ" value={summary.skipped} />
      </div>
      <div className="order-last flex w-full min-w-[160px] items-center gap-2 sm:order-none sm:ml-auto sm:w-auto sm:min-w-[200px] sm:flex-none">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full bg-green-500 transition-[width]"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="tabular-nums text-slate-600">{pct}%</span>
      </div>
    </div>
  );
}

function SummaryCounter({
  label,
  value,
  tone = 'slate',
  emphasise = false,
}: {
  label: string;
  value: number;
  tone?: 'slate' | 'green' | 'yellow' | 'red';
  emphasise?: boolean;
}) {
  const toneClass = {
    slate: 'text-slate-700',
    green: 'text-green-700',
    yellow: 'text-yellow-700',
    red: 'text-red-700',
  }[tone];
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-slate-500">{label}</span>
      <span
        className={cn(
          'tabular-nums font-semibold',
          toneClass,
          emphasise ? 'rounded bg-red-50 px-1.5' : '',
        )}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * A single applied-filter chip in the inline summary. Shows the condition
 * label only at rest; hovering reveals a ✗ overlaid on the top-right
 * corner that removes just this condition. Mirrors the hover/✗ affordance
 * the detail panel's dependency / delete actions use (PR #115 / #117).
 */
function AppliedFilterChipPill({
  chip,
  onRemove,
}: {
  chip: AppliedFilterChip;
  onRemove: () => void;
}) {
  return (
    <span
      data-testid="task-list-applied-filter-chip"
      className="group/chip relative inline-flex max-w-[180px] shrink-0 items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700"
    >
      <span className="truncate" title={chip.label}>
        {chip.label}
      </span>
      <button
        type="button"
        data-testid="task-list-applied-filter-remove"
        onClick={onRemove}
        aria-label={`${chip.label} を解除`}
        className="absolute -right-1 -top-1 hidden items-center justify-center rounded-full border border-slate-300 bg-white p-0.5 text-slate-500 shadow-sm hover:text-slate-900 group-hover/chip:flex"
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </span>
  );
}

interface FilterOption {
  value: string;
  label: string;
  /**
   * Optional status-tint utilities (`bg-* text-*`) applied to the chip
   * when it is NOT selected, so the status palette reads even at rest.
   * Selected chips fall back to the shared dark "active" treatment.
   */
  tintClass?: string;
}

function FilterChipGroup({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: readonly FilterOption[];
  selected: readonly string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="mr-1 text-slate-500">{label}:</span>
      {options.map((opt) => {
        const active = selected.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onToggle(opt.value)}
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium transition-colors',
              active
                ? 'border-slate-900 bg-slate-900 text-white'
                : opt.tintClass
                ? `border-transparent ${opt.tintClass} hover:brightness-95`
                : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * 完了基準バナー — 「対象フェーズ」フィルタで 1 つだけ選ばれているとき、
 * 該当フェーズ (`WbsItemDoc` with `kind === 'phase'`) の
 * `completionCriteria` を要約表示する。複数選択中・未選択時は描画なし
 * （行密度を守る）。本文は折りたたみ式で、デフォルトは閉。WBS ページ
 * への編集導線を貼り、空のときは「設定する」リンクを置く。
 */
function PhaseCompletionCriteriaBanner({
  projectId,
  selectedPhaseLabels,
  wbsItems,
}: {
  projectId: string;
  selectedPhaseLabels: readonly string[];
  wbsItems: readonly import('@mct/shared').WbsItemDoc[];
}) {
  const [open, setOpen] = useState(false);

  // 1 つだけ選んでいるときに限り該当フェーズを解決する。
  // 2 つ以上 / 未選択ならバナーを出さない（行密度の確保）。
  const phase = useMemo(() => {
    if (selectedPhaseLabels.length !== 1) return null;
    const label = selectedPhaseLabels[0]!;
    return (
      wbsItems.find(
        (i) => i.kind === 'phase' && i.name === label,
      ) ?? null
    );
  }, [wbsItems, selectedPhaseLabels]);

  if (!phase) return null;

  const criteria = (phase.completionCriteria ?? '').trim();
  const hasCriteria = criteria.length > 0;
  const focusUrl = `/projects/${projectId}/wbs?focus=${encodeURIComponent(phase.itemId)}`;

  return (
    <div
      data-testid="task-list-completion-criteria-banner"
      className="border-b border-slate-200 bg-sky-50/60 px-3 py-1.5 text-xs sm:px-4"
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="task-list-completion-criteria-toggle"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-slate-700 hover:text-slate-900"
          aria-expanded={open}
        >
          {open ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
          <span className="font-semibold">完了基準</span>
          <span className="text-slate-500">
            （{phase.name}
            {hasCriteria ? ` / ${criteria.length}字` : ''}）
          </span>
        </button>
        {hasCriteria ? (
          <Link
            to={focusUrl}
            className="ml-auto inline-flex items-center gap-1 text-sky-700 hover:underline"
          >
            フェーズ画面で編集
          </Link>
        ) : (
          <Link
            to={focusUrl}
            data-testid="task-list-completion-criteria-empty-link"
            className="ml-auto inline-flex items-center gap-1 text-sky-700 hover:underline"
          >
            設定する
          </Link>
        )}
      </div>
      {open ? (
        hasCriteria ? (
          <p
            data-testid="task-list-completion-criteria-body"
            className="mt-1.5 whitespace-pre-wrap text-slate-700"
          >
            {criteria}
          </p>
        ) : (
          <p
            data-testid="task-list-completion-criteria-empty"
            className="mt-1.5 text-slate-400"
          >
            未設定
          </p>
        )
      ) : null}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-500">
      {message}
    </div>
  );
}
